"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowUpRight } from "lucide-react";
import { cn, brandNameToSlug } from "@/lib/utils";
import { DisplayProduct } from "@/types";
import { motion } from "framer-motion";

interface ProductCardProps {
  product: DisplayProduct;
  brandName: string;
  index?: number;
  className?: string;
  collapsed?: boolean;
  topTags?: string;
  bottomButtons?: React.ReactNode;
}

export function ProductCard({
  product,
  brandName,
  index = 0,
  className,
  collapsed = false,
  topTags,
  bottomButtons,
}: ProductCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const isXcelr8 = brandName.toLowerCase().includes("xcelr8");

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{
        type: "spring",
        stiffness: 250,
        damping: 35,
        delay: index * 0.1,
      }}
      className={className}
    >
      <Link
        href={`/${brandNameToSlug(
          brandName
        )}/products/${product.part_number.toLowerCase()}`}
        className='block h-full'
      >
        {collapsed ? (
          // Collapsed card - now using React state for hover just like the full card
          <Card
            className='h-full border border-border/50 shadow-none overflow-hidden hover:bg-accent/50 hover:border-muted transition-colors rounded-2xl relative p-0'
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div className='aspect-square relative overflow-hidden bg-muted/50 rounded-2xl'>
              <Image
                src={
                  isHovered && product.hover_image
                    ? product.hover_image
                    : product.images && product.images.length > 0
                      ? product.images[0]
                      : "/placeholder-product.png"
                }
                alt={product.title}
                fill
                className={cn(
                  "object-contain transition-all duration-300",
                  isHovered && "scale-110"
                )}
                sizes='(max-width: 640px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 25vw, 20vw'
                loading='lazy'
                quality={65}
              />
              <div className='absolute top-4 right-4 bg-background/80 backdrop-blur-sm px-3 py-1 rounded-full'>
                <span className='text-sm font-medium'>
                  {product.part_number.toUpperCase()}
                </span>
              </div>

              {/* SVG Button - now using React state for hover */}
              <div
                className={cn(
                  "absolute bottom-0 right-0 w-16 h-16 transform translate-x-2 translate-y-2 transition-all duration-300",
                  isHovered && "scale-[1.2]",
                  "origin-bottom-right"
                )}
              >
                <svg viewBox='0 0 1080 1080' className='w-full h-full'>
                  <path
                    className={isXcelr8 ? "fill-[#f6eb0f]" : "fill-primary"}
                    d='M1080,1080H0L278.42,213.12C319.2,86.13,437.32,0,570.7,0h509.3v1080Z'
                  />
                </svg>
                <ArrowUpRight
                  className={cn(
                    "w-5 h-5 absolute top-1/2 left-1/2 -translate-x-1/3 -translate-y-1/2 transition-transform",
                    isHovered && "scale-125",
                    isXcelr8 ? "text-black" : "text-primary-foreground"
                  )}
                />
              </div>
            </div>
          </Card>
        ) : (
          // Full card - use React state for hover
          <Card
            className='h-full border border-border/50 p-4 shadow-none overflow-hidden hover:bg-accent/50 hover:border-muted transition-colors rounded-2xl relative group flex flex-col'
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {/* Product Image */}
            <div className='aspect-square w-full relative overflow-hidden rounded-xl mb-6 bg-muted/50 h-[300px]'>
              <Image
                src={
                  isHovered && product.hover_image
                    ? product.hover_image
                    : product.images && product.images.length > 0
                      ? product.images[0]
                      : "/placeholder-product.png"
                }
                alt={product.title}
                fill
                className={cn(
                  "object-contain  transition-all duration-300",
                  isHovered && "scale-110"
                )}
                sizes='(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw'
                loading='lazy'
                quality={65}
              />
              {topTags ? (
                <div className='absolute top-4 left-4 flex gap-2'>
                  <div className=' bg-primary backdrop-blur-sm px-3 rounded-full'>
                    <span className='text-sm font-medium'>{topTags}</span>
                  </div>
                  <div className=' bg-background backdrop-blur-sm px-3 rounded-full'>
                    <span className='text-sm font-medium'>
                      {product.part_number.toUpperCase()}
                    </span>
                  </div>
                </div>
              ) : (
                <div className='absolute top-4 right-4 bg-background/80 backdrop-blur-sm px-3 py-1 rounded-full'>
                  <span className='text-sm font-medium'>
                    {product.part_number.toUpperCase()}
                  </span>
                </div>
              )}
            </div>

            {/* Product Info */}
            <CardContent className='px-1 pb-0 flex-1 flex flex-col'>
              <div className='flex-1'>
                <h2 className='text-2xl font-bold mb-2'>{product.title}</h2>
                {product.attribute_values &&
                  product.attribute_values.length > 0 && (
                    <div className='flex flex-wrap gap-2 mb-2'>
                      {product.attribute_values.map((attr) => (
                        <div
                          key={attr.id}
                          className='bg-accent text-accent-foreground px-3 py-1 rounded-full text-sm font-medium'
                        >
                          {attr.value}
                        </div>
                      ))}
                    </div>
                  )}
              </div>
              <div className='flex justify-between items-center gap-4 mt-auto'>
                {typeof product.price === "number" && (
                  <div className='border border-border text-foreground px-3 py-1.5 rounded-full'>
                    <span className='font-medium'>
                      A${product.price.toFixed(2)}{" "}
                      <span className='text-xs text-muted-foreground'>
                        excl. GST
                      </span>
                    </span>
                    {typeof product.compare_price === "number" &&
                      product.compare_price > product.price && (
                        <span className='text-muted-foreground text-sm line-through ml-2'>
                          A${product.compare_price.toFixed(2)}
                        </span>
                      )}
                  </div>
                )}
              </div>
            </CardContent>

            {/* Bottom custom buttons */}
            {bottomButtons && (
              <div className='mt-2 flex gap-2'>{bottomButtons}</div>
            )}

            <div className='absolute bottom-0 right-0 w-20 h-20 transform translate-x-2 translate-y-2 transition-all duration-300 group-hover:scale-[1.4] origin-bottom-right'>
              <svg viewBox='0 0 1080 1080' className='w-full h-full'>
                <path
                  className='fill-primary'
                  d='M1080,1080H0L278.42,213.12C319.2,86.13,437.32,0,570.7,0h509.3v1080Z'
                />
              </svg>
              <ArrowUpRight className='w-7 h-7 text-primary-foreground absolute top-1/2 left-1/2 -translate-x-1/3 -translate-y-1/2 transition-transform group-hover:scale-125' />
            </div>
          </Card>
        )}
      </Link>
    </motion.div>
  );
}

export default ProductCard;
